/** @jsxImportSource theme-ui */
import { Card, Container, Flex, Heading, Box, Spinner, Text } from 'theme-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { SyncAllCompaniesMailchimpUsers } from '@/containers/favoriteActions/SyncAllCompaniesMailchimpUsers';
import { useGetMailchimpSyncExecutionsQuery } from '@/store/apiService';
import { format, startOfDay } from 'date-fns';
import CustomDatePicker from '@/components/CustomDatePicker';
import Select from '@/components/Select';
import { fetchCompanies } from '@/api/companies';

const MailchimpPage: React.FC = () => {
    const [createdDate, setCreatedDate] = useState<Date | null>(null);
    const [selectedCompany, setSelectedCompany] = useState<{ value: string; label: string } | null>(
        null
    );
    const [companySearch, setCompanySearch] = useState('');
    const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);

    useEffect(() => {
        let active = true;
        (async () => {
            const data = await fetchCompanies({ Search: companySearch || undefined });
            if (!active) return;
            setCompanyOptions(
                data.Results?.map((c) => ({
                    value: c.Id,
                    label: `${c.Name} (${c.Id})`,
                })) ?? []
            );
        })();
        return () => {
            active = false;
        };
    }, [companySearch]);

    const queryParams = useMemo(
        () => ({
            // Use empty string when no date is set to avoid sending "CreatedDate=null" in the query string
            CreatedDate: createdDate ? startOfDay(createdDate).toISOString() : '',
            CompanyId: selectedCompany?.value,
        }),
        [createdDate, selectedCompany]
    );

    const {
        data: executions,
        isLoading,
        isError,
        refetch,
    } = useGetMailchimpSyncExecutionsQuery(queryParams);

    // Ensure we refetch when the date filter changes
    useEffect(() => {
        refetch();
    }, [createdDate, refetch]);

    return (
        <Container>
            <Flex sx={{ flexDirection: 'column', gap: 3 }}>
                <Card variant="widget" p={3}>
                    <SyncAllCompaniesMailchimpUsers />
                </Card>

                <Card variant="widget" p={3}>
                    <Heading as="h3" mb={3}>
                        Sync executions
                    </Heading>
                    <Flex sx={{ gap: 3, mb: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ minWidth: 220 }}>
                            <Text as="label" sx={{ fontSize: 1 }}>
                                Created date
                            </Text>
                            <CustomDatePicker
                                field={{
                                    name: 'createdDate',
                                    value: createdDate,
                                }}
                                variation="compact"
                                onChange={(value: Date | null) => {
                                    setCreatedDate(value);
                                }}
                                isClearable
                            />
                        </Box>
                        <Box sx={{ minWidth: 260 }}>
                            <Text as="label" sx={{ fontSize: 1 }}>
                                Company
                            </Text>
                            <Select
                                value={selectedCompany}
                                isClearable
                                placeholder="Select company..."
                                options={companyOptions}
                                // react-select onInputChange returns the new input value
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                onInputChange={(value) => {
                                    setCompanySearch(value);
                                    return value;
                                }}
                                // @ts-ignore react-select value typing
                                onChange={(option) => {
                                    // @ts-ignore react-select value typing
                                    setSelectedCompany(option);
                                    // Ensure executions are refetched immediately when company changes
                                    refetch();
                                }}
                            />
                        </Box>
                    </Flex>
                    {isLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Spinner size={20} />
                            <Text>Loading sync executions...</Text>
                        </Box>
                    )}
                    {isError && !isLoading && (
                        <Text variant="error">Could not load sync executions.</Text>
                    )}
                    {!isLoading && !isError && executions && executions.length === 0 && (
                        <Text>No sync executions found.</Text>
                    )}
                    {!isLoading && !isError && executions && executions.length > 0 && (
                        <Box as="table" sx={{ width: '100%', mt: 2 }}>
                            <Box as="thead">
                                <Box as="tr">
                                    <Box as="th">Execution Id</Box>
                                    <Box as="th">Created</Box>
                                    <Box as="th">CompanyId</Box>
                                    <Box as="th">Success count</Box>
                                    <Box as="th">Failed companies</Box>
                                    <Box as="th">Manual</Box>
                                    <Box as="th">Status</Box>
                                </Box>
                            </Box>
                            <Box as="tbody">
                                {executions.map((exec, index: number) => {
                                    const formatDate = (value: any) => {
                                        if (!value) return '';
                                        try {
                                            return format(new Date(value), 'yyyy-MM-dd HH:mm');
                                        } catch {
                                            return String(value);
                                        }
                                    };

                                    return (
                                        <Box
                                            as="tr"
                                            key={exec.MailchimpSyncExecutionId || index}
                                        >
                                            <Box as="td">{exec.MailchimpSyncExecutionId}</Box>
                                            <Box as="td">{formatDate(exec.CreatedDate)}</Box>
                                            <Box as="td">{exec.CompanyId}</Box>
                                            <Box as="td">{exec.SuccessCount}</Box>
                                            <Box as="td">
                                                {exec.FailedCompanyIds
                                                    ? exec.FailedCompanyIds.length
                                                    : 0}
                                            </Box>
                                            <Box as="td">{exec.Manual ? 'Manual' : 'Scheduled'}</Box>
                                            <Box as="td">{exec.Status}</Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </Card>
            </Flex>
        </Container>
    );
};

export default MailchimpPage;
